import {
  Button,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  ExpandableDialog,
  ExpandableDialogProps,
  useUncontrolledState,
} from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import { Fullscreen, Minimize, X } from "lucide-react";
import { useInboxSearchParams } from "../../hooks";
import { useCachedTags } from "@tellescope/tanstack-query";
import { InboxFilter } from "./InboxFilters";
import { useMemo } from "react";

export interface InboxFilterDialogProps extends Omit<ExpandableDialogProps, "children"> {}

export const InboxFilterDialog = ({
  expanded,
  setExpanded,
  open: providedOpen,
  setOpen: providedSetOpen,
  ...dialogProps
}: InboxFilterDialogProps) => {
  const { t } = useTranslation("inbox");
  const [inboxQueryParams, setInboxQueryParams] = useInboxSearchParams();
  const [isExpanded, setIsExpanded] = useUncontrolledState({
    state: expanded ?? false,
    setState: setExpanded,
  });
  const [open, setOpen] = useUncontrolledState({
    state: providedOpen ?? false,
    setState: providedSetOpen,
  });
  const { data: availableTags } = useCachedTags({ model: "inbox_threads" });

  const filterCount = useMemo<number>(() => {
    return Object.entries(inboxQueryParams).filter(([, value]) => !!value).length;
  }, [inboxQueryParams]);

  return (
    <ExpandableDialog
      {...dialogProps}
      expanded={isExpanded}
      setExpanded={setIsExpanded}
      hideCloseButton
      open={open}
      setOpen={setOpen}
      className="p-0 flex flex-col"
    >
      <DialogHeader className="flex flex-row items-center justify-between p-4 border-b border-b-gray-200 flex-shrink-0">
        <div className="flex flex-row items-center gap-2">
          <Button
            onClick={() => {
              setIsExpanded((prev) => !prev);
            }}
            variant="ghost"
            size="icon"
          >
            {isExpanded ? <Minimize /> : <Fullscreen />}
          </Button>
          <DialogTitle className="text-sm font-normal">{t("Filter Messages")}</DialogTitle>
        </div>
        <Button variant="ghost" size="icon" onClick={() => setOpen(false)}>
          <X />
        </Button>
        <DialogDescription className="sr-only">{t("Apply filters to narrow down your thread list")}</DialogDescription>
      </DialogHeader>
      <div className="pb-4 px-4 overflow-y-auto flex-1 min-h-0">
        <InboxFilter
          filters={{
            ...inboxQueryParams,
            dateRange: {
              from: inboxQueryParams.dateRange?.from ? new Date(inboxQueryParams.dateRange.from) : undefined,
              to: inboxQueryParams.dateRange?.to ? new Date(inboxQueryParams.dateRange.to) : undefined,
            },
          }}
          onFiltersChange={(newVal) => {
            setInboxQueryParams(({ dateRange, ...rest }) => {
              const calculatedNewVal =
                typeof newVal === "function"
                  ? newVal({
                      ...rest,
                      dateRange: {
                        from: dateRange?.from ? new Date(dateRange?.from) : undefined,
                        to: dateRange?.to ? new Date(dateRange?.to) : undefined,
                      },
                    })
                  : newVal;
              return {
                ...calculatedNewVal,
                dateRange:
                  calculatedNewVal.dateRange && (calculatedNewVal.dateRange.from || calculatedNewVal.dateRange.to)
                    ? {
                        from: calculatedNewVal.dateRange?.from?.toISOString(),
                        to: calculatedNewVal.dateRange?.to?.toISOString(),
                      }
                    : undefined,
              };
            });
          }}
          availableTags={availableTags}
        />
      </div>
      {filterCount > 0 && (
        <div className="absolute bottom-0 left-0 right-0 shrink-0 border-t bg-background px-4 py-4">
          <Button
            variant="outline"
            onClick={() => {
              setInboxQueryParams({});
            }}
            className="w-full"
          >
            {t("Clear All Filters")}
          </Button>
        </div>
      )}
    </ExpandableDialog>
  );
};
